import React, { useMemo } from 'react';
import {
  FlatList,
  Pressable,
  RefreshControl,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppInput } from '../../components/AppInput';
import { AppScreen } from '../../components/AppScreen';
import { EmptyState } from '../../components/EmptyState';
import { ErrorState } from '../../components/ErrorState';
import { MasterDataListItem } from '../../components/MasterDataListItem';
import { PaginationFooter } from '../../components/PaginationFooter';
import { PermissionGuard } from '../../components/PermissionGuard';
import { ScreenHeader } from '../../components/ScreenHeader';
import { getWorkPolicyModuleCapabilities } from '../../config/workPolicyCapabilities';
import { PERMISSIONS } from '../../constants/permissions';
import { routes, type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import { useWorkPolicies } from '../../hooks/useWorkPolicies';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';

type Props = NativeStackScreenProps<RootStackParamList, 'WorkPolicyList'>;

export function WorkPolicyListScreen({ navigation }: Props) {
  const palette = useAppColors();
  const themed = useMemo(
    () => ({
      chip: {
        borderWidth: 1,
        borderColor: palette.border,
        borderRadius: 999,
        paddingHorizontal: spacing.md,
        paddingVertical: spacing.xs,
        backgroundColor: palette.surface,
      },
      chipText: { ...typography.caption, color: palette.textPrimary },
      loading: { ...typography.body, color: palette.textSecondary },
    }),
    [palette],
  );

  const auth = useAuth();
  const capabilities = getWorkPolicyModuleCapabilities(auth.permissions);
  const list = useWorkPolicies();

  const footer = useMemo(
    () => <PaginationFooter loading={list.isLoadingMore} />,
    [list.isLoadingMore],
  );

  const hasFilters = Boolean(list.search.trim()) || list.filters.isActive != null;

  return (
    <PermissionGuard
      permission={PERMISSIONS.WORK_POLICY_VIEW}
      fallback={<UnauthorizedScreen />}
    >
      <AppScreen contentStyle={styles.screen}>
        <ScreenHeader
          title="Work policies"
          onBack={navigation.goBack}
        />
        <View style={styles.body}>
        <AppInput
          label="Search"
          value={list.search}
          onChangeText={list.setSearch}
          placeholder="Code or name"
          autoCapitalize="none"
        />
        <View style={styles.filterRow}>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Show active work policies"
            onPress={() => list.applyFilters({ isActive: true })}
            style={themed.chip}
          >
            <Text style={themed.chipText}>Active</Text>
          </Pressable>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Show inactive work policies"
            onPress={() => list.applyFilters({ isActive: false })}
            style={themed.chip}
          >
            <Text style={themed.chipText}>Inactive</Text>
          </Pressable>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Clear status filter"
            onPress={() => list.applyFilters({})}
            style={themed.chip}
          >
            <Text style={themed.chipText}>All</Text>
          </Pressable>
        </View>

        {capabilities.canCreateWorkPolicy ? (
          <AppButton
            title="Create work policy"
            onPress={() => navigation.navigate(routes.workPolicyCreate)}
          />
        ) : null}

        {list.isLoading && list.items.length === 0 ? (
          <View style={styles.center}>
            <Text style={themed.loading}>Loading work policies…</Text>
          </View>
        ) : list.error && list.items.length === 0 ? (
          <ErrorState message={list.error} onRetry={list.retry} />
        ) : (
          <FlatList
            data={list.items}
            keyExtractor={item => String(item.workPolicyId)}
            refreshControl={
              <RefreshControl
                refreshing={list.isRefreshing}
                onRefresh={list.refresh}
              />
            }
            onEndReached={list.loadMore}
            onEndReachedThreshold={0.4}
            ListFooterComponent={footer}
            ListEmptyComponent={
              <EmptyState
                title={
                  hasFilters
                    ? 'No records match the current search.'
                    : 'No work policies were found.'
                }
                message={
                  hasFilters
                    ? 'Try clearing search or filters.'
                    : 'Create a work policy when you have permission.'
                }
                actionTitle={hasFilters ? 'Reset filters' : 'Retry'}
                onAction={hasFilters ? list.resetFilters : list.retry}
              />
            }
            contentContainerStyle={styles.listContent}
            renderItem={({ item }) => (
              <MasterDataListItem
                title={item.policyName}
                code={item.policyCode}
                subtitle={[
                  item.defaultShiftName
                    ? `Shift: ${item.defaultShiftName}`
                    : null,
                  item.overtimeEnabled ? 'Overtime on' : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
                status={item.statusLabel}
                onPress={() =>
                  navigation.navigate(routes.workPolicyDetails, {
                    workPolicyId: item.workPolicyId,
                  })
                }
              />
            )}
          />
        )}

        </View>
      </AppScreen>
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, paddingBottom: 0 },
  body: { flex: 1, marginTop: spacing.lg, gap: spacing.sm },
  filterRow: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm },
  listContent: { flexGrow: 1, paddingBottom: spacing.xl, gap: spacing.sm },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center' },
});
